import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { BankAccountPicker, CustomerPicker, LedgerPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ArStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ArShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const studentLabel = (r, look) => {
  if (r?.customer) return r.customer.name || `Customer ${r.customer_id}`;
  return look.customerLabel(r?.customer_id, `Customer ${r?.customer_id ?? ''}`);
};

const emptyForm = () => ({
  customer_id: '', write_off_id: '', recovery_date: new Date().toISOString().slice(0, 10),
  amount: '', received_into_account_id: '',
});

// Recovering an amount previously written off (a "bad debt" student pays
// after all). Same field-contract caveat as WriteOffsTab: this endpoint
// isn't exercised elsewhere in the repo. DRAFT -> APPROVED -> POSTED
// lifecycle; approve/void run as checker, create/post as maker.
const RecoveriesTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [writeOffs, setWriteOffs] = useState([]);
  const [loadingWriteOffs, setLoadingWriteOffs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!values.customer_id) {
      setWriteOffs([]);
      return;
    }
    let alive = true;
    setLoadingWriteOffs(true);
    ledgerAPI.getArWriteOffs({ customer_id: values.customer_id, status: 'POSTED', limit: 100 })
      .then((res) => { if (alive) setWriteOffs(unwrapList(res)); })
      .catch(() => { if (alive) setWriteOffs([]); })
      .finally(() => { if (alive) setLoadingWriteOffs(false); });
    return () => { alive = false; };
  }, [values.customer_id]);

  const writeOffOptions = writeOffs.map((w) => ({
    value: w.write_off_id,
    label: `${w.write_off_number || `WO-${w.write_off_id}`} - ${formatAccountingAmount(w.amount)}`,
  }));

  const selectWriteOff = (writeOffId) => {
    const w = writeOffs.find((x) => String(x.write_off_id) === String(writeOffId));
    setValues((p) => ({ ...p, write_off_id: writeOffId, amount: w ? String(w.amount) : p.amount }));
  };

  const submitCreate = async () => {
    if (!values.customer_id) {
      showSnackbar('Student is required', 'error');
      return;
    }
    if (!values.write_off_id) {
      showSnackbar('Select the write-off being recovered', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!values.received_into_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    const payload = {
      customer_id: Number(values.customer_id),
      write_off_id: Number(values.write_off_id),
      recovery_date: values.recovery_date,
      amount: Number(values.amount),
      received_into_account_id: Number(values.received_into_account_id),
    };
    try {
      setSaving(true);
      await ledgerAPI.createArRecovery(payload);
      showSnackbar('Recovery created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this recovery?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Recovery ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getArRecovery(row.recovery_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch recovery'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidArRecovery(voidDlg.item.recovery_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Recovery voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.RECEIVABLES}
        title="Recoveries"
        subtitle="Amounts recovered from students after their balance was written off"
        addLabel="New Recovery"
        idKey="recovery_id"
        searchPlaceholder="Search recovery #, student..."
        emptyMessage="No recoveries found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getArRecoveries(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'customer_id', label: 'Student', picker: CustomerPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Recovery date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'amount' },
        ]}
        columns={[
          { label: 'Recovery #', render: (r) => <strong>{r.recovery_number || `RCV-${r.recovery_id}`}</strong> },
          { label: 'Student', render: (r) => studentLabel(r, look) },
          { label: 'Write-off', render: (r) => r.writeOff?.write_off_number || `WO-${r.write_off_id}`, hideOnMobile: true },
          { label: 'Date', render: (r) => fmtDate(r.recovery_date) },
          { label: 'Amount', render: (r) => formatAccountingAmount(r.amount), align: 'right' },
          { label: 'Status', render: (r) => <ArStatusChip status={r.status} /> },
        ]}
        rowActions={(r) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(r) }];
          if (r.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveArRecovery(r.recovery_id)) });
          }
          if (r.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postArRecovery(r.recovery_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(r.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: r, void_reason: '' }) });
          }
          if (r.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete recovery',
                message: `Delete DRAFT recovery ${r.recovery_number || `RCV-${r.recovery_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteArRecovery(r.recovery_id);
                    showSnackbar('Recovery deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Recovery</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <CustomerPicker
                label="Student" value={values.customer_id}
                onChange={(id) => setValues({ ...emptyForm(), customer_id: id })}
                required
              />
            </Grid>
            <Grid size={12}>
              {!values.customer_id ? (
                <Typography variant="body2" color="text.secondary">Select a student to load their posted write-offs.</Typography>
              ) : loadingWriteOffs ? (
                <Typography variant="body2" color="text.secondary">Loading write-offs...</Typography>
              ) : writeOffs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No posted write-offs for this student.</Typography>
              ) : (
                <LedgerPicker label="Write-off being recovered" value={values.write_off_id} onChange={selectWriteOff} options={writeOffOptions} required />
              )}
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="date" label="Recovery Date" value={values.recovery_date}
                onChange={(e) => setValues((p) => ({ ...p, recovery_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={12}>
              <BankAccountPicker label="Received Into" value={values.received_into_account_id} onChange={(id) => setValues((p) => ({ ...p, received_into_account_id: id }))} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Recovery {detail?.recovery_number || (detail?.recovery_id ? `RCV-${detail.recovery_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Student: <strong>{studentLabel(detail, look)}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Write-off: {detail.writeOff?.write_off_number || `WO-${detail.write_off_id}`}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Date: {fmtDate(detail.recovery_date)}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Amount: <strong>{formatAccountingAmount(detail.amount)}</strong></Typography>
              <Box sx={{ mt: 1 }}><ArStatusChip status={detail.status} /></Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>Voided: {detail.void_reason}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void recovery ${voidDlg.item?.recovery_number || (voidDlg.item ? `RCV-${voidDlg.item.recovery_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default RecoveriesTab;
